import React, { useEffect, useState } from "react";
import InvoiceTable from "./InvoiceTable";
import { plusIcon } from "../../../Base/SVG";
import ChartOuter from "./Chart/ChartOuter";
import InvoiceModal from "./InvoiceModal";
import { AnimatePresence, motion } from "framer-motion";
import CreateModal from "./CreateModal";
import useWallet from "../../../utils/hooks/useWalletConnect";
import useSignature from "../../../utils/hooks/useSignature";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInvoiceHistory,
  fetchPaymentRequestStats,
} from "../../../redux/invoice/action";
import { createInvoice } from "../../../redux/invoice/api";
import { statsType, timeframe } from "../../../utils/constants";
import useCustomToast from "../../../utils/hooks/useToast";
import { toast } from "react-toastify";
import { webBaseURL } from "../../../utils/config";
import CopyModal from "./copyModal";

export default function Invoice() {
  const { account, isConnected, connectWallet } = useWallet();
  const { isSignIn, getAuth, generateSignature } = useSignature();
  const { showToast } = useCustomToast();

  const dispatch = useDispatch();
  const {
    history: { data, loading, error },
  } = useSelector(({ invoice }) => invoice);
  const { stats } = useSelector(({ invoice }) => invoice);

  const { signature, nonce } = useSelector(({ user }) => user);
  // this selected is for period
  const [timePeriod, setTimePeriod] = useState(timeframe[0]);
  const [typeOfStats, setTypeOfStats] = useState(statsType[0]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [modal, setModal] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [historyList, setHistoryList] = useState([]);
  const [invoiceLinkId, setInvoiceLinkId] = useState(null);
  const [offset, setOffset] = useState(0);

  const lazyDummy = () => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  useEffect(() => {
    lazyDummy();
  }, []);

  useEffect(() => {
    dispatch(fetchPaymentRequestStats(timePeriod, typeOfStats, account));
  }, [timePeriod, typeOfStats, account]);

  useEffect(() => {
    if (modal) document.body.classList.add("active");
    else document.body.classList.remove("active");
  }, [modal]);

  useEffect(() => {
    setHistoryList(data);
  }, [data]);

  useEffect(() => {
    if (!!account && !!signature) {
      getHistoryData();
    }
  }, [account, signature, selectedStatus, offset]);

  useEffect(() => {
    if (data?.list?.length) {
      data?.list.forEach((s) => {
        s.paymentDate = s?.expiresAt;
        s.invoiceLink = `${webBaseURL}/landing?invoice=${s.id}`;
        s.nftLink = s?.payment?.nftId;
        s.txLink = s?.payment?.hash;
        s.token = s.payment?.token?.name;
        s.blockchain = s?.payment?.networkId;
        return s;
      });
    }
    setHistoryList({ ...data });
  }, [data]);

  const getHistoryData = () => {
    const payload = {
      offset,
      limit: 6,
      $auth: getAuth,
    };
    if (selectedStatus) {
      payload.status = selectedStatus;
    }
    dispatch(fetchInvoiceHistory(payload));
  };

  const onSave = async (data) => {
    let loadingToast = null;

    try {
      if (!data.amount || !data.description || !data.expirationDate) {
        return showToast("Please enter all the fields.", "error");
      }
      const payload = {
        amountUsd: data.amount,
        reason: data.description,
        expiresAt: new Date(data.expirationDate).toISOString(),
        $auth: getAuth,
      };

      // Show the toast with a loading indicator
      loadingToast = toast.loading("Creating request...", {
        autoClose: false,
      });
      // Manually capture the promise result
      const invoiceResult = await createInvoice(payload);
      if (invoiceResult?.status == 201) {
        toast.dismiss(loadingToast);
        toast.success("Request created successfully!");
        setInvoiceLinkId(invoiceResult?.data?.id);
        getHistoryData();
        setModal("copy");
      } else {
        toast.error("Failed to create request!");
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Failed to create request!");
      console.log(error);
    }
  };

  return (
    <>
      <div className="invoice">
        <div className="auto__container">
          {isLoading ? (
            <>
              <h3 className="placeholder"></h3>
              <div className="invoice__head">
                <div className="chart placeholder"></div>
                <div className="chart placeholder"></div>
              </div>
              <h3 className="placeholder"></h3>
              <div className="invoiceTable__header">
                <div className="search placeholder"></div>
                <div className="invoiceTable__header-action">
                  <button type="button" className="button placeholder"></button>
                  <button type="button" className="button placeholder"></button>
                </div>
              </div>
              <div className="invoiceTable box">
                <div className="invoiceTable__body">
                  <div className="placeholder"></div>
                  {[0, 1, 2, 3].map((item, index) => {
                    return <div className="placeholder" key={index}></div>;
                  })}
                </div>
              </div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              exit={{ opacity: 0, y: 10 }}
            >
              <h3>Dashboard</h3>

              <div className="invoice__head">
                <div className="invoice__head-item">
                  <div className="invoice__head-content">
                    <h4 className="big">Create new payment request</h4>
                    <p>
                      Create a new payment request with and receive the payment
                      in cryptocurrency directly to your wallet. Say goodbye to
                      complicated payment processes and start accepting payments
                      with just a few clicks.
                    </p>
                    <button
                      className="button white"
                      onClick={() => {
                        if (isConnected && !isSignIn) {
                          generateSignature();
                        } else if (!isConnected && !isSignIn) {
                          connectWallet();
                        } else {
                          setModal("create");
                        }
                      }}
                    >
                      {plusIcon} Create Request
                    </button>
                  </div>
                  <div className="invoice__head-image">
                    <img
                      src={process.env.PUBLIC_URL + "/images/coinBug.png"}
                      alt="coinBug"
                    />
                  </div>
                </div>
                <ChartOuter
                  list={timeframe}
                  timePeriod={timePeriod}
                  setTimePeriod={setTimePeriod}
                  setTypeOfStats={setTypeOfStats}
                  typeOfStats={typeOfStats}
                  stats={stats.data}
                />
              </div>
              <div className="invoice__inner">
                <h3>Invoice History</h3>
                <InvoiceTable
                  selectedStatus={selectedStatus}
                  setSelectedStatus={setSelectedStatus}
                  historyList={historyList}
                  setModalData={setModalData}
                  setModal={setModal}
                  offset={offset}
                  setOffset={setOffset}
                />
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <AnimatePresence>
        {modal === "details" && (
          <InvoiceModal
            close={() => {
              setModal(null);
              setModalData(null);
            }}
            title={
              <>
                <span className="purple"></span> Receipt
              </>
            }
            {...modalData}
          />
        )}
        {modal === "create" && (
          <CreateModal onClose={() => setModal(null)} onSave={onSave} />
        )}
        {modal === "copy" && (
          <CopyModal
            onClose={() => {
              setModal(null);
              setInvoiceLinkId(null);
            }}
            invoiceLinkId={invoiceLinkId}
          />
        )}
      </AnimatePresence>
    </>
  );
}
